import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboard } from '../services/api';
import { Users, Building2, UserCheck, Briefcase, CreditCard, FileText, AlertTriangle, TrendingUp } from 'lucide-react';

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={16} className="text-white" />
      </div>
    </div>
    <div className="text-2xl font-semibold text-gray-900">{value ?? '—'}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const AlertRow = ({ message, type }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg text-sm
    ${type === 'danger' ? 'bg-red-50 text-red-700' :
      type === 'warn'   ? 'bg-amber-50 text-amber-700' :
      'bg-blue-50 text-blue-700'}`}>
    <AlertTriangle size={15} />
    <span>{message}</span>
  </div>
);

const Dashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn : () => getDashboard().then(r => r.data.data)
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      Loading dashboard...
    </div>
  );

  const w = data?.workforce    || {};
  const f = data?.finance      || {};
  const a = data?.alerts       || {};
  const r = data?.recruitment  || {};

  return (
    <div className="space-y-6">

      {/* Page Title */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-400 mt-0.5">Live overview of your staffing operations</p>
      </div>

      {/* Workforce Stats */}
      <div>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Workforce</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Candidates"   value={w.total_candidates}   sub="in database"      icon={Users}      color="bg-blue-500" />
          <StatCard label="Deployed"           value={w.active_deployments} sub="currently active"  icon={UserCheck}  color="bg-green-500" />
          <StatCard label="On Bench"           value={w.on_bench}           sub="available now"     icon={Users}      color="bg-amber-500" />
          <StatCard label="Open Positions"     value={r.open_positions}     sub="pending fulfilment" icon={Briefcase}  color="bg-purple-500" />
        </div>
      </div>

      {/* Finance Stats */}
      <div>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Finance — This Month</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Revenue Billed"   value={f.this_month_revenue   ? `₹${Number(f.this_month_revenue).toLocaleString('en-IN')}`   : '₹0'} sub={f.revenue_growth_pct ? `${f.revenue_growth_pct > 0 ? '↑' : '↓'} ${Math.abs(f.revenue_growth_pct)}% vs last month` : ''} icon={TrendingUp}  color="bg-blue-600" />
          <StatCard label="Collected"        value={f.this_month_collected ? `₹${Number(f.this_month_collected).toLocaleString('en-IN')}` : '₹0'} sub="payments received"   icon={CreditCard}  color="bg-green-600" />
          <StatCard label="Payroll Cost"     value={f.this_month_payroll   ? `₹${Number(f.this_month_payroll).toLocaleString('en-IN')}`   : '₹0'} sub="salaries processed"  icon={CreditCard}  color="bg-orange-500" />
          <StatCard label="Active Clients"   value={data?.clients?.active}  sub={`of ${data?.clients?.total} total`} icon={Building2}   color="bg-teal-500" />
        </div>
      </div>

      {/* Alerts + Recent */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Alerts */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Alerts</h2>
          <div className="space-y-2">
            {a.overdue_invoices > 0 && (
              <AlertRow message={`${a.overdue_invoices} overdue invoice(s) need attention`} type="danger" />
            )}
            {a.expiring_contracts > 0 && (
              <AlertRow message={`${a.expiring_contracts} contract(s) expiring in 30 days`} type="warn" />
            )}
            {a.pending_leaves > 0 && (
              <AlertRow message={`${a.pending_leaves} leave request(s) pending approval`} type="info" />
            )}
            {!a.overdue_invoices && !a.expiring_contracts && !a.pending_leaves && (
              <div className="text-sm text-gray-400 text-center py-4">No alerts 🎉</div>
            )}
          </div>
        </div>

        {/* Recent Deployments */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Recent Deployments</h2>
          <div className="space-y-3">
            {data?.recent_deployments?.length > 0 ? (
              data.recent_deployments.map((dep) => (
                <div key={dep.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-xs font-semibold">
                      {dep.candidates?.first_name?.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {dep.candidates?.first_name} {dep.candidates?.last_name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{dep.clients?.company_name}</p>
                  </div>
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    {dep.status}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-400 text-center py-4">No deployments yet</div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;